import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  useMediaQuery,
  useTheme,
  Container,
  IconButton,
  CircularProgress,
  Grid,
  Divider,
  Autocomplete,
} from "@mui/material";
import {
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import appointmentService from "../services/appointmentService";
import userService from "../services/userService";
import tenantService from "../services/tenantService";
import tenantUsersService from "../services/tenantUsersService";
import serviceService from "../services/serviceService";
import categoryService from "../services/categoryService";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
// import { getUserTypeFromConnections } from "../utils/userHelper"; // Nije korišćeno
import { colors } from "../theme/colors";

const AppointmentBooking = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Refs za auto-scroll do selektovane usluge
  const serviceRefs = useRef({});
  
  // Ref za čuvanje quickCustomerName da se ne gubi na re-render
  const quickCustomerNameRef = useRef("");
  const inputRef = useRef(null);

  const appointmentId = params.id; // 'new' or actual ID
  const isEdit = appointmentId && appointmentId !== 'new';

  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d;
  }, []);

  // State-ovi
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [quickCustomerMode, setQuickCustomerMode] = useState(true);
  const [quickCustomerName, setQuickCustomerName] = useState("");
  // Lokalni state za mobilni input koji se ažurira odmah
  const [quickCustomerNameLocal, setQuickCustomerNameLocal] = useState("");
  
  
  // Vrati vrednost iz ref-a ako se state neočekivano resetuje
  useEffect(() => {
    if (!quickCustomerName && quickCustomerNameRef.current) {
      console.log('🔧 Restoring quickCustomerName from ref:', quickCustomerNameRef.current);
      setQuickCustomerName(quickCustomerNameRef.current);
      setQuickCustomerNameLocal(quickCustomerNameRef.current);
    }
  }, [quickCustomerName]);
  
  // Resetuj lokalni state kada se quickCustomerMode promeni
  useEffect(() => {
    if (!quickCustomerMode) {
      setQuickCustomerNameLocal("");
    }
  }, [quickCustomerMode]);
  const [quickCustomerConfirmed, setQuickCustomerConfirmed] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [availableDays, setAvailableDays] = useState(null);
  const [availableSlots, setAvailableSlots] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  

  const handleCancelAppointment = useCallback(async () => {
    if (!editingAppointment?._id) return;
    try {
      await appointmentService.cancelAppointment(editingAppointment._id, "Otkazano od strane korisnika");
      toast.success("Termin je otkazan");
      navigate('/appointments');
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      toast.error(err.response?.data?.message || err.message || 'Greška pri otkazivanju termina');
    }
  }, [editingAppointment?._id, navigate]);

  const [formData, setFormData] = useState({
    customerId: "",
    employeeId: "",
    serviceIds: [],
    tenantId: "",
    appointmentDate: tomorrow,
    duration: 30,
    totalPrice: 0,
    status: "pending",
    notes: {
      customer: "",
      employee: "",
    },
  });

  // Helper funkcije
  const getCurrentTenantId = useCallback(() => {
    if (currentUser?.tenantId) {
      return currentUser.tenantId?._id || currentUser.tenantId;
    }
    if (currentUser?.tenantConnections && currentUser.tenantConnections.length > 0) {
      const firstConnection = currentUser.tenantConnections[0];
      return firstConnection?.tenantId?._id || firstConnection?.tenantId;
    }
    return null;
  }, [currentUser]);

  const getUserTypeFromCurrentTenant = useCallback(() => {
    if (!currentUser?.tenantConnections || currentUser.tenantConnections.length === 0) {
      return currentUser?.userType;
    }
    if (selectedTenantId) {
      const connection = currentUser.tenantConnections.find(
        tc => {
          const tcTenantId = tc.tenantId?._id || tc.tenantId;
          return tcTenantId === selectedTenantId;
        }
      );
      if (connection?.userType) {
        return connection.userType;
      }
    }
    const firstConnection = currentUser.tenantConnections[0];
    return firstConnection?.userType || currentUser?.userType;
  }, [currentUser, selectedTenantId]);

  // Helper funkcija za dobijanje dostupnih zaposlenih
  const getAvailableEmployees = useCallback(() => {
    const currentTenantId = selectedTenantId || getCurrentTenantId();
    if (!currentTenantId) return [];
    
    // Normalizuj tenantId za poređenje (string)
    const normalizedCurrentTenantId = typeof currentTenantId === 'object' 
      ? (currentTenantId._id || currentTenantId) 
      : currentTenantId;
    
    return users.filter((user) => {
      if (user.userType !== "employee" && user.userType !== "tenant_admin") {
        return false;
      }
      // Normalizuj user tenantId za poređenje
      const userTenantId = user.tenantId?._id || user.tenantId || user.tenantConnections?.[0]?.tenantId?._id || user.tenantConnections?.[0]?.tenantId;
      const normalizedUserTenantId = typeof userTenantId === 'object' 
        ? (userTenantId._id || userTenantId) 
        : userTenantId;
      
      return String(normalizedUserTenantId) === String(normalizedCurrentTenantId);
    });
  }, [users, selectedTenantId, getCurrentTenantId]);

  const combineDateAndTime = (date, timeString) => {
    if (!date || !timeString) return date;
    const [hours, minutes] = timeString.split(":").map((v) => parseInt(v, 10));
    console.log('timeString:', timeString, '=> hours:', hours, 'minutes:', minutes);
    // Koristi lokalno vreme - postavi samo sate i minute bez promene vremenske zone
    const combined = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours || 0, minutes || 0, 0, 0);
    return combined;
  };

  const isDateNonWorking = useCallback((date) => {
    if (!date || !availableDays) return false;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    
    if (availableDays.data && availableDays.data.availableDates) {
      const isAvailable = availableDays.data.availableDates.some(
        (availableDate) => availableDate.date === dateStr
      );
      if (isAvailable) {
        return false;
      }
    }
    // Disabled-uj sve datume koji nisu available, uključujući fully_booked
    return true;
  }, [availableDays]);

  // Helper funkcija koja proverava da li je datum fully_booked
  const isDateFullyBooked = useCallback((date) => {
    if (!date || !availableDays) return false;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    if (availableDays.data && availableDays.data.unavailableDates) {
      const unavailableDate = availableDays.data.unavailableDates.find(
        (unavailableDate) => unavailableDate.date === dateStr
      );
      if (unavailableDate) {
        const isFullyBooked = (
          unavailableDate.reason === 'fully_booked' || 
          unavailableDate.category === 'fully_booked' ||
          unavailableDate.reason === 'fullyBooked' ||
          unavailableDate.category === 'fullyBooked'
        );
        if (isFullyBooked) {
          console.log('Fully booked date detected:', dateStr, unavailableDate);
        }
        return isFullyBooked;
      }
    }
    return false;
  }, [availableDays]);

  const getCustomerName = (appointment) => {
    if (appointment.isQuickAppointment && appointment.customerName) {
      return appointment.customerName;
    }
    const customerId = appointment.customerId;
    if (customerId && typeof customerId === "object") {
      return (
        `${customerId.firstName || ""} ${customerId.lastName || ""}`.trim() ||
        "N/A"
      );
    }
    const customer = users.find((u) => (u._id || u.id) === customerId);
    return customer ? `${customer.firstName} ${customer.lastName}` : "N/A";
  };

  // Helper funkcija za filtrirane korisnike
  const getFilteredCustomers = useMemo(() => {
    return users.filter((user) => {
      if (user.userType !== "customer") return false;
      if (user.isActive === false) return false;
      if (currentUser?.userType === "super_admin" && selectedTenantId) {
        return user.tenantId === selectedTenantId;
      }
      return true;
    });
  }, [users, currentUser, selectedTenantId]);

  // Memoized values
  const categoryRefs = React.useRef({});
  const servicesByCategory = useMemo(() => {
    const map = {};
    services.forEach((s) => {
      const catId =
        typeof s.categoryId === "object" ? s.categoryId?._id : s.categoryId;
      if (!map[catId]) map[catId] = [];
      map[catId].push(s);
    });
    return map;
  }, [services]);

  const [additionalTime, setAdditionalTime] = useState(0);

  const totalDuration = useMemo(() => {
    const baseDuration = selectedServiceIds
      .map((id) => services.find((s) => s._id === id)?.duration || 0)
      .reduce((a, b) => a + b, 0);
    return baseDuration + (additionalTime || 0);
  }, [selectedServiceIds, services, additionalTime]);

  const totalPrice = useMemo(() => {
    return selectedServiceIds
      .map((id) => services.find((s) => s._id === id)?.price?.amount || 0)
      .reduce((a, b) => a + b, 0);
  }, [selectedServiceIds, services]);

  const timeSlots = useMemo(() => {
    if (
      !availableSlots ||
      !availableSlots.data ||
      !availableSlots.data.availableSlots
    ) {
      const slots = [];
      const startMinutes = 9 * 60;
      const endMinutes = 20 * 60 + 30;
      for (let m = startMinutes; m <= endMinutes; m += 30) {
        const hours = Math.floor(m / 60)
          .toString()
          .padStart(2, "0");
        const minutes = (m % 60).toString().padStart(2, "0");
        slots.push(`${hours}:${minutes}`);
      }
      return slots;
    }
    return availableSlots.data.availableSlots || [];
  }, [availableSlots]);

  // Fetch funkcije
  const fetchCurrentTenant = useCallback(async (tenantId) => {
    try {
      const response = await tenantService.getTenantById(tenantId);
      setCurrentTenant(response);
    } catch (err) {
      console.error("Error fetching current tenant:", err);
      setCurrentTenant(null);
    }
  }, []);

  const fetchAvailableDays = useCallback(
    async (tenantId, serviceDuration = null) => {
      try {
        if (!tenantId || typeof tenantId !== "string") {
          console.error("Invalid tenantId:", tenantId);
          return;
        }
        const params = {};
        if (serviceDuration && typeof serviceDuration === "number") {
          params.duration = serviceDuration;
        }
        const response = await appointmentService.getAvailableDays(
          tenantId,
          params
        );
        setAvailableDays(response);
      } catch (err) {
        console.error("Error fetching available days:", err);
        setAvailableDays(null);
      }
    },
    []
  );

  const fetchAvailableSlots = useCallback(
    async (tenantId, date, serviceDuration = null, employeeIdArg = null) => {
      try {
        if (!tenantId || typeof tenantId !== "string") {
          console.error("Invalid tenantId:", tenantId);
          return;
        }
        if (!date) {
          console.error("Invalid date:", date);
          return;
        }
        const params = {};
        if (serviceDuration && typeof serviceDuration === "number") {
          params.duration = serviceDuration;
        }
        const effectiveEmployeeId = employeeIdArg || formData.employeeId;
        if (effectiveEmployeeId) {
          params.employeeId = effectiveEmployeeId;
        }
        const response = await appointmentService.getAvailableSlotsForDate(
          tenantId,
          date,
          params
        );
        setAvailableSlots(response);
      } catch (err) {
        console.error("Error fetching available slots:", err);
        setAvailableSlots(null);
      }
    },
    [formData.employeeId]
  );

  const fetchCategories = useCallback(async (tenantId) => {
    if (!tenantId) {
      setCategories([]);
      return;
    }

    try {
      console.log("📋 fetchCategories called for tenantId:", tenantId);
      const response = await categoryService.getCategoriesByTenant(tenantId, 1, 9999);
      console.log("📋 Categories response:", response);

      // Normalizacija odgovora - kopirano iz Appointments.js
      let cats = [];
      if (Array.isArray(response)) {
        cats = response;
      } else if (response && Array.isArray(response.categories)) {
        cats = response.categories;
      } else if (response && response.data && Array.isArray(response.data)) {
        cats = response.data;
      } else if (response && response.data && Array.isArray(response.data?.categories)) {
        cats = response.data.categories;
      } else if (response?.serviceCategories && Array.isArray(response.serviceCategories)) {
        cats = response.serviceCategories;
      }

      console.log("📋 Setting categories:", cats.length);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  }, []);

  const fetchServices = useCallback(async (tenantId) => {
    if (!tenantId) {
      setServices([]);
      return;
    }

    try {
      console.log("🔧 fetchServices called for tenantId:", tenantId);
      const response = await serviceService.getServicesByTenant(tenantId, 1, 9999);
      console.log("🔧 Services response:", response);

      // Normalizacija odgovora - kopirano iz Appointments.js
      const servicesData = response.services || response;
      const allServices = Array.isArray(servicesData) ? servicesData : [];
      
      // Filtriraj samo aktivne usluge
      const activeServices = allServices.filter(service => service.isActive === true);

      console.log("🔧 Setting services:", activeServices.length, "active out of", allServices.length, "total");
      setServices(activeServices);
    } catch (error) {
      console.error("Error fetching services:", error);
      setServices([]);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const tenantId = getCurrentTenantId();

      let response;
      if (isSuperAdmin) {
        if (selectedTenantId) {
          response = await tenantUsersService.getTenantUsers({
            tenantId: selectedTenantId,
            page: 1,
            limit: 1000,
            includeEmployees: true, // Za AppointmentBooking treba i zaposleni i korisnici
          });
        } else {
          response = await userService.getAllUsers();
        }
      } else if ((isTenantAdmin || actualUserType === "customer" || actualUserType === "employee") && tenantId) {
        response = await tenantUsersService.getTenantUsers({
          tenantId,
          page: 1,
          limit: 1000,
          includeEmployees: true, // Za AppointmentBooking treba i zaposleni i korisnici
        });
      } else if (tenantId) {
        response = await tenantUsersService.getTenantUsers({
          tenantId,
          page: 1,
          limit: 1000,
          includeEmployees: true, // Za AppointmentBooking treba i zaposleni i korisnici
        });
      } else {
        setUsers([]);
        return;
      }

      const usersToSet = response.users || response;
      setUsers(Array.isArray(usersToSet) ? usersToSet : []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  }, [selectedTenantId, getCurrentTenantId, getUserTypeFromCurrentTenant]);

  const fetchTenants = useCallback(async () => {
    try {
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const isCustomer = !actualUserType || actualUserType === "customer";
      const tenantId = getCurrentTenantId();

      const isEmployee = actualUserType === "employee";

      if (isSuperAdmin) {
        const response = await tenantService.getAllTenants();
        setTenants(response.tenants || response);
      } else if ((isTenantAdmin || isCustomer || isEmployee) && tenantId) {
        const response = await tenantService.getTenantById(tenantId);
        setTenants([response]);
      } else if (tenantId) {
        const response = await tenantService.getTenantById(tenantId);
        setTenants([response]);
      } else {
        setTenants([]);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
      setTenants([]);
    }
  }, [getCurrentTenantId, getUserTypeFromCurrentTenant]);

  // Inicijalizacija - učitaj appointment ako je edit mode
  useEffect(() => {
    const initializeAppointment = async () => {
      setLoading(true);
      try {
        const actualUserType = getUserTypeFromCurrentTenant();
        const isCustomer = actualUserType === "customer";
        const isSuperAdmin = actualUserType === "super_admin";
        const isEmployee = actualUserType === "employee";

        // Učitaj tenante i korisnike
        await fetchTenants();
        await fetchUsers();

        if (isEdit && appointmentId && appointmentId !== 'new') {
          // Edit mode - učitaj appointment
          const appointment = await appointmentService.getAppointmentById(appointmentId);
          setEditingAppointment(appointment);

          // Postavi serviceIds
          let serviceIds = [];
          if (appointment.serviceIds && appointment.serviceIds.length > 0) {
            serviceIds = appointment.serviceIds.map((service) =>
              typeof service === "object" ? service._id : service
            );
          } else if (appointment.serviceId) {
            serviceIds = [appointment.serviceId._id || appointment.serviceId];
          }
          setSelectedServiceIds(serviceIds);

          // Postavi selectedTime
          if (appointment.selectedTime) {
            setSelectedTime(appointment.selectedTime);
          }

          // Postavi additionalTime
          const additionalTimeValue = appointment?.additionalTime != null 
            ? parseInt(appointment.additionalTime, 10) || 0 
            : 0;
          setAdditionalTime(additionalTimeValue);

          // Postavi formData
          const tenantId = appointment.tenantId?._id || appointment.tenantId;
          setSelectedTenantId(tenantId || "");

          setQuickCustomerMode(false);
          setQuickCustomerName("");

          setFormData({
            customerId: appointment.customerId?._id || appointment.customerId || "",
            employeeId: appointment.employeeId?._id || appointment.employeeId || "",
            serviceIds: serviceIds,
            tenantId: tenantId || "",
            appointmentDate: new Date(appointment.appointmentDate || appointment.startTime || new Date()),
            duration: appointment.totalDuration || appointment.duration || 30,
            totalPrice: appointment.totalPrice || appointment.price?.amount || appointment.amount || 0,
            status: appointment.status || "pending",
            notes: {
              customer: appointment.notes?.customer || "",
              employee: appointment.notes?.employee || "",
            },
          });

          // Postavi selectedTenantId - useEffect će automatski učitati kategorije i usluge
          if (tenantId) {
            setSelectedTenantId(tenantId);
            await fetchCurrentTenant(tenantId);
            // Ne pozivaj fetchCategories i fetchServices ovde - useEffect će to uraditi
          }
        } else {
          // New appointment mode
          setEditingAppointment(null);
          setSelectedServiceIds([]);
          setSelectedTime("");
          setAdditionalTime(0);

          const customerIdToSet = isCustomer ? currentUser._id : "";
          const tenantIdToSet = isSuperAdmin ? "" : getCurrentTenantId();
          const employeeIdToSet = isEmployee ? currentUser._id : "";

          setSelectedTenantId(tenantIdToSet || "");
          setFormData({
            customerId: customerIdToSet,
            employeeId: employeeIdToSet,
            serviceIds: [],
            tenantId: tenantIdToSet || "",
            appointmentDate: tomorrow,
            duration: 30,
            totalPrice: 0,
            status: "pending",
            notes: {
              customer: "",
              employee: "",
            },
          });

          // Postavi quickCustomerMode
          if (!isCustomer) {
            const tenantId = tenantIdToSet || getCurrentTenantId();
            if (tenantId) {
              const tenant = await tenantService.getTenantById(tenantId);
              setCurrentTenant(tenant);
              if (tenant?.settings?.enableQuickAppointment) {
                setQuickCustomerMode(tenant.settings.defaultAppointmentMode === "quick");
              } else {
                setQuickCustomerMode(false);
              }
            } else {
              setQuickCustomerMode(true);
            }
          } else {
            setQuickCustomerMode(false);
          }

          // Postavi selectedTenantId - useEffect će automatski učitati kategorije i usluge
          if (tenantIdToSet) {
            setSelectedTenantId(tenantIdToSet);
            await fetchCurrentTenant(tenantIdToSet);
            // Ne pozivaj fetchCategories i fetchServices ovde - useEffect će to uraditi
          }
        }
      } catch (error) {
        console.error("Error initializing appointment:", error);
        toast.error("Greška pri učitavanju podataka");
        navigate('/appointments');
      } finally {
        setLoading(false);
      }
    };

    initializeAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, isEdit]); // Uklonio currentUser?._id

  // Ažuriraj customerId kada se currentUser promeni (ali ne resetuj ostala polja)
  useEffect(() => {
    const actualUserType = getUserTypeFromCurrentTenant();
    const isCustomer = actualUserType === "customer";
    
    if (isCustomer && currentUser?._id && !isEdit) {
      setFormData(prev => ({ ...prev, customerId: currentUser._id }));
    }
  }, [currentUser?._id, isEdit, getUserTypeFromCurrentTenant]);

  // Učitaj kategorije i usluge kada se promeni selectedTenantId
  // Ovo je najvažniji useEffect - mora se izvršiti kada se selectedTenantId promeni
  useEffect(() => {
    // Ne učitavaj dok je u toku inicijalizacija
    if (loading) {
      return;
    }

    if (selectedTenantId && typeof selectedTenantId === "string") {
      console.log("🔄 [useEffect] selectedTenantId changed to:", selectedTenantId);
      console.log("🔄 Fetching categories and services...");
      fetchCategories(selectedTenantId);
      fetchServices(selectedTenantId);
      setSelectedCategoryId("");
      // Ne briši selektovane usluge ako uređujemo postojeći termin
      if (!editingAppointment) {
        setSelectedServiceIds([]);
      }
    } else {
      // Resetuj samo ako je super_admin i nema izabranog tenanta
      if (currentUser?.userType === "super_admin" && !selectedTenantId) {
        console.log("🔄 Clearing categories and services (super_admin, no tenant)");
        setCategories([]);
        setServices([]);
        setSelectedCategoryId("");
        setSelectedServiceIds([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, loading]);

  // Kada su usluge učitane i imamo selekciju (posebno u edit modu), skroluj na prvu selektovanu
  useEffect(() => {
    if (!loading && selectedServiceIds && selectedServiceIds.length > 0 && isEdit && editingAppointment) {
      const firstSelectedId = selectedServiceIds[0];
      const el = serviceRefs.current[firstSelectedId];
      if (el && typeof el.scrollIntoView === "function") {
        // timeout kako bi se osiguralo da je DOM spreman posle rendera
        setTimeout(() => {
          try {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          } catch (_) {
            el.scrollIntoView();
          }
        }, 0);
      }
    }
  }, [loading, selectedServiceIds, services, categories, isEdit, editingAppointment]);

  // Fetch podataka kada se promeni selectedTenantId
  useEffect(() => {
    if (selectedTenantId && typeof selectedTenantId === "string") {
      // na promenu tenanta, povuci korisnike i tenante po istim pravilima
      fetchUsers();
      fetchTenants();
      fetchCurrentTenant(selectedTenantId);
    } else {
      setAvailableDays(null);
      setAvailableSlots(null);
    }
  }, [selectedTenantId, fetchCurrentTenant, fetchUsers, fetchTenants]);

  // Postavi currentTenant kada se promeni selectedTenantId
  useEffect(() => {
    if (selectedTenantId && tenants.length > 0) {
      const tenant = tenants.find((t) => t._id === selectedTenantId);
      if (tenant && (!currentTenant || currentTenant._id !== tenant._id)) {
        // Postavi tenant samo ako se stvarno promenio
        console.log("🏢 Setting currentTenant from tenants array:", tenant.name);
        setCurrentTenant(tenant);
        if (!editingAppointment && !quickCustomerName.trim() && !quickCustomerNameRef.current) {
          // Samo resetuj mode ako korisnik nije već počeo da kuca ime
          const actualUserType = getUserTypeFromCurrentTenant();
          const isCustomer = actualUserType === "customer";
          if (isCustomer) {
            setQuickCustomerMode(false);
          } else {
            if (tenant.settings?.enableQuickAppointment) {
              setQuickCustomerMode(tenant.settings.defaultAppointmentMode === "quick");
            } else {
              setQuickCustomerMode(false);
            }
          }
        }
      }
    } else if (!selectedTenantId) {
      setCurrentTenant(null);
    }
  }, [selectedTenantId, tenants, editingAppointment, getUserTypeFromCurrentTenant, quickCustomerName, currentTenant]);

  // Dodaj stilove za fully_booked datume direktno u DOM
  useEffect(() => {
    if (!availableDays) return;
    
    const styleId = 'fully-booked-days-styles';
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    styleElement.textContent = `
      .MuiPickersDay-root.fully-booked-day,
      .MuiPickersDay-root[data-fully-booked='true'] {
        color: #d32f2f !important;
      }
      .MuiPickersDay-root.fully-booked-day.Mui-disabled,
      .MuiPickersDay-root[data-fully-booked='true'].Mui-disabled {
        color: #d32f2f !important;
        opacity: 1 !important;
        cursor: not-allowed !important;
      }
    `;
    
    // Funkcija koja direktno stilizuje fully_booked datume u DOM-u
    const styleFullyBookedDays = () => {
      if (!availableDays?.data?.unavailableDates) return;
      
      // Pronađi sve PickersDay elemente
      const dayElements = document.querySelectorAll('.MuiPickersDay-root:not(.MuiPickersDay-dayOutsideMonth)');
      
      // Pokušaj da pronađeš mesec i godinu iz kalendara
      const calendarHeader = document.querySelector('.MuiPickersCalendarHeader-root');
      let year = new Date().getFullYear();
      let month = new Date().getMonth() + 1;
      
      // Pokušaj da pročitaš mesec i godinu iz header-a ako je moguće
      if (calendarHeader) {
        const headerText = calendarHeader.textContent || '';
        // Pokušaj da parsiraš mesec i godinu iz teksta (npr. "January 2026")
        const monthMatch = headerText.match(/(\w+)\s+(\d{4})/);
        if (monthMatch) {
          const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                             'july', 'august', 'september', 'october', 'november', 'december'];
          const monthName = monthMatch[1].toLowerCase();
          const monthIndex = monthNames.findIndex(m => m.startsWith(monthName));
          if (monthIndex !== -1) {
            month = monthIndex + 1;
            year = parseInt(monthMatch[2]);
          }
        }
      }
      
      dayElements.forEach((element) => {
        const dayText = element.textContent?.trim();
        if (!dayText || isNaN(dayText)) return;
        
        // Proveri da li je datum fully_booked
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayText).padStart(2, '0')}`;
        
        const unavailableDate = availableDays.data.unavailableDates.find(
          (ud) => ud.date === dateStr
        );
        
        const isFullyBooked = unavailableDate && (
          unavailableDate.reason === 'fully_booked' || 
          unavailableDate.category === 'fully_booked' ||
          unavailableDate.reason === 'fullyBooked' ||
          unavailableDate.category === 'fullyBooked'
        );
        
        if (isFullyBooked) {
          element.style.setProperty('color', '#d32f2f', 'important');
          element.style.setProperty('opacity', '1', 'important');
          element.setAttribute('data-fully-booked', 'true');
          element.classList.add('fully-booked-day');
        }
      });
    };
    
    // Pozovi odmah i nakon kratkog delay-a
    styleFullyBookedDays();
    const timeoutId1 = setTimeout(styleFullyBookedDays, 100);
    const timeoutId2 = setTimeout(styleFullyBookedDays, 500);
    const intervalId = setInterval(styleFullyBookedDays, 1000);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearInterval(intervalId);
      const element = document.getElementById(styleId);
      if (element) {
        element.remove();
      }
    };
  }, [availableDays]);

  // Ažuriraj formData kada se promene izabrane usluge
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      serviceIds: selectedServiceIds,
      duration: totalDuration,
      totalPrice: totalPrice,
    }));

    if (selectedTenantId && selectedServiceIds.length > 0) {
      const requiresEmployee = currentTenant?.settings?.autoAssignEmployee === false;
      if (!requiresEmployee || (requiresEmployee && formData.employeeId)) {
        fetchAvailableDays(selectedTenantId, totalDuration);
      } else {
        setAvailableDays(null);
      }
    }
  }, [
    selectedServiceIds,
    selectedTenantId,
    fetchAvailableDays,
    totalDuration,
    totalPrice,
    currentTenant?.settings?.autoAssignEmployee,
    formData.employeeId,
  ]);

  // Fetch dostupnih slotova kada se promeni datum
  useEffect(() => {
    if (
      formData.appointmentDate &&
      selectedTenantId &&
      selectedServiceIds.length > 0
    ) {
      const requiresEmployee = currentTenant?.settings?.autoAssignEmployee === false;
      if (!requiresEmployee || (requiresEmployee && formData.employeeId)) {
        const d = new Date(formData.appointmentDate);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const dateStr = `${y}-${m}-${day}`;
        fetchAvailableSlots(
          selectedTenantId,
          dateStr,
          totalDuration,
          formData.employeeId
        );
      } else {
        setAvailableSlots(null);
      }
    }
  }, [
    formData.appointmentDate,
    selectedTenantId,
    selectedServiceIds,
    totalDuration,
    fetchAvailableSlots,
    currentTenant?.settings?.autoAssignEmployee,
    formData.employeeId,
  ]);

  // Očisti dostupne dane/slotove kada je potreban zaposleni a nije odabran
  useEffect(() => {
    const requiresEmployee = currentTenant?.settings?.autoAssignEmployee === false;
    if (requiresEmployee && !formData.employeeId) {
      setAvailableDays(null);
      setAvailableSlots(null);
    }
  }, [currentTenant?.settings?.autoAssignEmployee, formData.employeeId]);

  // Automatski setuj zaposlenog ako ima samo jedan
  useEffect(() => {
    const requiresEmployee = currentTenant?.settings?.autoAssignEmployee === false;
    if (requiresEmployee && !formData.employeeId && !editingAppointment) {
      const availableEmployees = getAvailableEmployees();
      if (availableEmployees.length === 1) {
        console.log('🔄 Auto-setting single employee:', availableEmployees[0].firstName, availableEmployees[0].lastName);
        setFormData(prev => ({ ...prev, employeeId: availableEmployees[0]._id }));
      }
    }
  }, [currentTenant?.settings?.autoAssignEmployee, formData.employeeId, editingAppointment, getAvailableEmployees]);

  // Handle functions
  const handleClose = () => {
    navigate('/appointments');
  };

  console.log('form data', formData)
  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const isEmployee = actualUserType === "employee";

      const tenantId = getCurrentTenantId();

      const dataToSend = {
        ...formData,
        tenantId: !isSuperAdmin && tenantId ? tenantId : formData.tenantId,
        serviceIds: selectedServiceIds,
        duration: totalDuration,
        totalPrice: totalPrice,
        selectedTime: selectedTime,
        ...((isSuperAdmin || isTenantAdmin || isEmployee) && { additionalTime: parseInt(additionalTime, 10) || 0 }),
        actualUserType: actualUserType,
      };

      // Ako je korisnik zaposleni i employeeId nije postavljen, postavi ga na currentUser._id
      if (isEmployee && (!dataToSend.employeeId || dataToSend.employeeId === "")) {
        dataToSend.employeeId = currentUser._id;
      }

      if (editingAppointment) {
        if (editingAppointment.isQuickAppointment) {
          dataToSend.customerName = editingAppointment.customerName;
          dataToSend.isQuickAppointment = true;
          delete dataToSend.customerId;
        } else {
          dataToSend.isQuickAppointment = false;
          delete dataToSend.customerName;
        }
      } else if (
        currentTenant?.settings?.enableQuickAppointment &&
        quickCustomerMode
      ) {
        dataToSend.customerName = quickCustomerNameRef.current || quickCustomerName;
        dataToSend.isQuickAppointment = true;
        delete dataToSend.customerId;
      } else {
        dataToSend.isQuickAppointment = false;
        delete dataToSend.customerName;
      }

      delete dataToSend.status;

      if (editingAppointment) {
        await appointmentService.updateAppointment(
          editingAppointment._id,
          dataToSend
        );
        toast.success("Termin je uspešno ažuriran!");
      } else {
        await appointmentService.createAppointment(dataToSend);
        toast.success("Termin je uspešno kreiran!");
      }

      navigate('/appointments');
    } catch (err) {
      toast.error(err.message || "Greška pri kreiranju/ažuriranju termina");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ width: "100%", height: isMobile ? "auto" : "calc(100vh - 64px)", minHeight: isMobile ? "calc(100vh - 64px)" : "auto", py: { xs: 2, md: 3 } }}>
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 3 }, height: isMobile ? "auto" : "100%", pb: { xs: isMobile ? 16 : 0, md: 0 } }}>
        <Grid container spacing={3} sx={{ height: isMobile ? "auto" : "100%" }}>
          {/* LEVA STRANA - Glavni sadržaj */}
          <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : '100%' }}>
            {/* HEADER - transformisan iz DialogTitle */}
            <Box
              sx={{
                mb: 3,
                pb: 2.5,
                position: isMobile ? "relative" : "sticky",
                top: isMobile ? 0 : 64,
                zIndex: 1101,
                background: isDarkMode
                  ? "linear-gradient(135deg, rgba(37, 99, 235, 0.18), rgba(17, 24, 39, 0.6))"
                  : "linear-gradient(135deg, rgba(59, 130, 246, 0.14), rgba(255,255,255,0.7))",
                backdropFilter: "blur(10px)",
                borderRadius: 2,
                borderBottom: `1px solid ${customColors.border.light}`,
                px: { xs: 2, md: 3 },
                pt: { xs: 2, md: 3 },
                boxShadow: isDarkMode
                  ? "0 2px 12px rgba(0,0,0,0.2)"
                  : "0 2px 12px rgba(0,0,0,0.05)",
              }}
            >
        {/* Prvi red - Back button, naslov i step indicator */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          {/* Back button */}
          <IconButton
            onClick={handleClose}
            sx={{
              color: commonStyles.textSecondary.color,
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Naslov */}
          {!isMobile && (
            <Typography 
              variant="h5" 
              sx={{
                ...commonStyles.textPrimary,
                fontWeight: 600,
                flex: 1,
                textAlign: "center",
              }}
            >
              {editingAppointment ? "Izmeni termin" : "Novi termin"}
            </Typography>
          )}

          {/* Modern step indicator */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {[1, 2, 3].map((step, index) => (
              <Box
                key={step}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: currentStep >= step ? 36 : 28,
                    height: currentStep >= step ? 36 : 28,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: currentStep >= step ? "0.875rem" : "0.75rem",
                    fontWeight: currentStep >= step ? 700 : 500,
                    transition: "all 0.3s ease",
                    background:
                      currentStep > step
                        ? `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`
                        : currentStep === step
                        ? customColors.primary.main
                        : isDarkMode
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.05)",
                    color:
                      currentStep >= step
                        ? "white"
                        : commonStyles.textSecondary.color,
                    border:
                      currentStep < step
                        ? `2px solid ${customColors.border.light}`
                        : "none",
                    boxShadow:
                      currentStep === step
                        ? `0 4px 12px ${customColors.primary.main}40`
                        : "none",
                  }}
                >
                  {currentStep > step ? (
                    <CheckIcon sx={{ fontSize: 18 }} />
                  ) : (
                    step
                  )}
                </Box>
                {index < 2 && (
                  <Box
                    sx={{
                      width: currentStep > step ? 24 : 16,
                      height: 2,
                      transition: "all 0.3s ease",
                      background:
                        currentStep > step
                          ? `linear-gradient(90deg, ${customColors.primary.main}, ${customColors.primary.dark})`
                          : isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.1)",
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {isMobile ? (
          // Mobilni header - kontrolni elementi
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Prvi red - Tenant i Klijent */}
            {currentStep === 1 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  transition: "all 0.3s ease-in-out",
                }}
              >
                {/* Tenant */}
                {currentUser?.userType === "super_admin" && (
                  <>
                    {selectedTenantId ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          backgroundColor: isDarkMode
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                          borderRadius: 1,
                          border: `1px solid ${customColors.border.light}`,
                          cursor: "pointer",
                          minWidth: "fit-content",
                        }}
                        onClick={() => {
                          setSelectedTenantId("");
                          setFormData({
                            ...formData,
                            customerId: "",
                            employeeId: "",
                            tenantId: "",
                          });
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={commonStyles.textPrimary}
                        >
                          {tenants.find((t) => t._id === selectedTenantId)?.name}
                        </Typography>
                        <CloseIcon
                          sx={{
                            fontSize: 16,
                            color: commonStyles.textSecondary.color,
                          }}
                        />
                      </Box>
                    ) : (
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={commonStyles.textSecondary}>
                          Tenant
                        </InputLabel>
                        <Select
                          value={selectedTenantId}
                          label="Tenant"
                          onChange={(e) => {
                            const newTenantId = e.target.value;
                            console.log("🏢 Tenant selected:", newTenantId);
                            setSelectedTenantId(newTenantId);
                            setFormData((prev) => ({
                              ...prev,
                              customerId: "",
                              employeeId: "",
                              tenantId: newTenantId,
                            }));
                          }}
                          disabled={editingAppointment}
                          sx={commonStyles.selectField}
                          MenuProps={{
                            PaperProps: {
                              sx: commonStyles.selectMenu,
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Odaberite tenant</em>
                          </MenuItem>
                          {tenants.map((tenant) => (
                            <MenuItem key={tenant._id} value={tenant._id}>
                              {tenant.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}

                {/* Klijent */}
                {(() => {
                  const actualUserType = getUserTypeFromCurrentTenant();
                  return actualUserType && actualUserType !== "customer";
                })() && (
                  <>
                    {(
                      editingAppointment
                        ? editingAppointment.isQuickAppointment
                          ? editingAppointment.customerName
                          : getCustomerName(editingAppointment)
                        : quickCustomerMode
                        ? quickCustomerName.trim()
                        : formData.customerId
                        ? users.find((u) => u._id === formData.customerId)?.firstName +
                          " " +
                          users.find((u) => u._id === formData.customerId)?.lastName
                        : ""
                    ) ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          backgroundColor: isDarkMode
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                          borderRadius: 1,
                          border: `1px solid ${customColors.border.light}`,
                          cursor: "pointer",
                          minWidth: "fit-content",
                        }}
                        onClick={() => {
                          if (editingAppointment) return;
                          if (quickCustomerMode) {
                            // Obriši input direktno preko ref-a
                            if (inputRef.current) {
                              inputRef.current.value = "";
                            }
                            quickCustomerNameRef.current = "";
                            setQuickCustomerName("");
                            setQuickCustomerConfirmed(false);
                          } else {
                            setFormData({ ...formData, customerId: "" });
                          }
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={commonStyles.textPrimary}
                        >
                          {editingAppointment
                            ? editingAppointment.isQuickAppointment
                              ? editingAppointment.customerName
                              : getCustomerName(editingAppointment)
                            : quickCustomerMode
                            ? quickCustomerName.trim()
                            : formData.customerId
                            ? users.find((u) => u._id === formData.customerId)?.firstName +
                              " " +
                              users.find((u) => u._id === formData.customerId)?.lastName
                            : ""}
                        </Typography>
                        {!editingAppointment && (
                          <CloseIcon
                            sx={{
                              fontSize: 16,
                              color: commonStyles.textSecondary.color,
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ minWidth: 200 }}>
                        {editingAppointment ? (
                          editingAppointment.isQuickAppointment ? (
                            <TextField
                              fullWidth
                              label="Ime osobe"
                              value={editingAppointment.customerName || ""}
                              disabled={true}
                              sx={commonStyles.formField}
                            />
                          ) : (
                            <Autocomplete
                              options={getFilteredCustomers}
                              value={getFilteredCustomers.find((u) => (u._id || u.id) === formData.customerId) || null}
                              getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName}` : getCustomerName(editingAppointment)}
                              disabled={true}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Klijent"
                                  sx={commonStyles.formField}
                                />
                              )}
                              sx={{ width: "100%" }}
                            />
                          )
                        ) : currentTenant?.settings?.enableQuickAppointment ? (
                          quickCustomerMode ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "flex-end",
                              }}
                            >
                              <TextField
                                fullWidth
                                label="Ime osobe"
                                inputRef={inputRef}
                                value={isMobile ? quickCustomerNameLocal : (quickCustomerNameRef.current || quickCustomerName || "")}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  quickCustomerNameRef.current = newValue;
                                  // Na mobilnom ažuriraj samo lokalni state dok korisnik kuca
                                  if (isMobile) {
                                    setQuickCustomerNameLocal(newValue);
                                  } else {
                                    // Na desktopu ažuriraj glavni state odmah
                                    setQuickCustomerName(newValue);
                                  }
                                  // Auto-confirm on mobile when name is entered
                                  if (newValue.trim()) {
                                    setQuickCustomerConfirmed(true);
                                  } else {
                                    setQuickCustomerConfirmed(false);
                                  }
                                }}
                                onBlur={(e) => {
                                  const finalValue = e.target.value;
                                  // Na mobilnom, sync lokalni state sa glavnim state-om
                                  if (isMobile) {
                                    setQuickCustomerNameLocal(finalValue);
                                    setQuickCustomerName(finalValue);
                                  } else {
                                    // Na desktopu samo osiguraj da je state ažuriran
                                    setQuickCustomerName(finalValue);
                                  }
                                }}
                                placeholder="Unesite ime osobe za termin"
                                sx={commonStyles.formField}
                                disabled={editingAppointment}
                              />
                              {quickCustomerName.trim() &&
                                quickCustomerConfirmed && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      minWidth: "auto",
                                      px: 1,
                                      color: colors.status.appointment.confirmed,
                                    }}
                                  >
                                    <CheckIcon />
                                  </Box>
                                )}
                            </Box>
                          ) : (
                            <Autocomplete
                              options={getFilteredCustomers}
                              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                              value={getFilteredCustomers.find((u) => (u._id || u.id) === formData.customerId) || null}
                              onChange={(event, newValue) => {
                                setFormData({
                                  ...formData,
                                  customerId: newValue ? (newValue._id || newValue.id) : "",
                                });
                              }}
                              disabled={
                                editingAppointment ||
                                (currentUser?.userType === "super_admin" &&
                                  !selectedTenantId)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Klijent"
                                  sx={commonStyles.formField}
                                />
                              )}
                              sx={{ width: "100%" }}
                            />
                          )
                        ) : (
                          <Autocomplete
                            options={getFilteredCustomers}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                            value={getFilteredCustomers.find((u) => (u._id || u.id) === formData.customerId) || null}
                            onChange={(event, newValue) => {
                              setFormData({
                                ...formData,
                                customerId: newValue ? (newValue._id || newValue.id) : "",
                              });
                            }}
                            disabled={
                              editingAppointment ||
                              (currentUser?.userType === "super_admin" &&
                                !selectedTenantId)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Klijent"
                                sx={commonStyles.formField}
                              />
                            )}
                            sx={{ width: "100%" }}
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Zaposleni - samo na mobilnom */}
            {currentStep === 1 &&
              isMobile &&
              (() => {
                let actualUserType = currentUser?.userType;
                if (!actualUserType && currentUser?.tenantConnections?.length > 0) {
                  const connection = currentUser.tenantConnections.find(
                    tc => (tc.tenantId?._id || tc.tenantId) === selectedTenantId
                  ) || currentUser.tenantConnections[0];
                  actualUserType = connection?.userType;
                }
                const isCustomer = actualUserType === "customer";
                const isTenantAdmin = actualUserType === "tenant_admin";
                const isSuperAdmin = actualUserType === "super_admin";
                const requiresEmployee = (isCustomer || isTenantAdmin || isSuperAdmin) &&
                  !currentTenant?.settings?.autoAssignEmployee &&
                  selectedTenantId;
                
                if (!requiresEmployee) return false;
                
                // Ne prikazuj dropdown ako ima samo jedan zaposleni
                const availableEmployees = getAvailableEmployees();
                const shouldShow = availableEmployees.length > 1;
                
                return shouldShow;
              })() && (
                <Box sx={{ width: "100%" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={commonStyles.textSecondary}>
                      Zaposleni
                    </InputLabel>
                    <Select
                      value={formData.employeeId}
                      label="Zaposleni"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          employeeId: e.target.value,
                        })
                      }
                      sx={commonStyles.selectField}
                    >
                      <MenuItem value="">
                        <em>Izaberite zaposlenog</em>
                      </MenuItem>
                      {getAvailableEmployees().map((employee) => (
                          <MenuItem key={employee._id} value={employee._id}>
                            {`${employee.firstName} ${employee.lastName}`}
                            {employee.shift ? ` (${employee.shift})` : ""}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

            {/* Toggle dugmad */}
            {(() => {
              const actualUserType = getUserTypeFromCurrentTenant();
              const shouldShowToggle = actualUserType && actualUserType !== "customer" &&
                currentTenant?.settings?.enableQuickAppointment &&
                !editingAppointment &&
                currentStep === 1 &&
                !(editingAppointment
                  ? editingAppointment.isQuickAppointment
                    ? editingAppointment.customerName
                    : getCustomerName(editingAppointment)
                  : quickCustomerMode
                  ? quickCustomerName.trim()
                  : formData.customerId
                  ? users.find((u) => u._id === formData.customerId)?.firstName +
                    " " +
                    users.find((u) => u._id === formData.customerId)?.lastName
                  : "");
              
              if (!shouldShowToggle) return null;

              return (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexDirection: "row",
                    justifyContent: "flex-start",
                  }}
                >
                  <Button
                    size="small"
                    variant={quickCustomerMode ? "contained" : "outlined"}
                    onClick={() => {
                      setQuickCustomerMode(true);
                      setFormData({ ...formData, customerId: "" });
                      setQuickCustomerConfirmed(false);
                    }}
                    sx={{
                      fontSize: "0.75rem",
                      px: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      transition: "all 0.3s ease",
                      ...(quickCustomerMode
                        ? commonStyles.gradientButton
                        : commonStyles.outlinedButton),
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    Neregistrovani korisnik
                  </Button>
                  <Button
                    size="small"
                    variant={!quickCustomerMode ? "contained" : "outlined"}
                    onClick={() => {
                      setQuickCustomerMode(false);
                      setQuickCustomerName("");
                      setQuickCustomerConfirmed(false);
                    }}
                    sx={{
                      fontSize: "0.75rem",
                      px: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      transition: "all 0.3s ease",
                      ...(!quickCustomerMode
                        ? commonStyles.gradientButton
                        : commonStyles.outlinedButton),
                      "&:hover": {
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    Registrovan korisnik
                  </Button>
                </Box>
              );
            })()}
          </Box>
        ) : (
          // Desktop header - kontrolni elementi
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 2,
            }}
          >
            {currentStep === 1 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                  width: "auto",
                }}
              >
                {/* Tenant dropdown */}
                {currentUser?.userType === "super_admin" && (
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel sx={commonStyles.textSecondary}>
                      Tenant
                    </InputLabel>
                    <Select
                      value={selectedTenantId}
                      label="Tenant"
                      onChange={(e) => {
                        const newTenantId = e.target.value;
                        console.log("🏢 Tenant selected (desktop):", newTenantId);
                        setSelectedTenantId(newTenantId);
                        setFormData((prev) => ({
                          ...prev,
                          customerId: "",
                          employeeId: "",
                          tenantId: newTenantId,
                        }));
                      }}
                      disabled={editingAppointment}
                      sx={commonStyles.selectField}
                      MenuProps={{
                        PaperProps: {
                          sx: commonStyles.selectMenu,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Odaberite tenant</em>
                      </MenuItem>
                      {tenants.map((tenant) => (
                        <MenuItem key={tenant._id} value={tenant._id}>
                          {tenant.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Toggle dugmad */}
                {(() => {
                  const actualUserType = getUserTypeFromCurrentTenant();
                  if (actualUserType && actualUserType !== "customer" &&
                    currentTenant?.settings?.enableQuickAppointment &&
                    !editingAppointment) {
                    return true;
                  }
                  return false;
                })() && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexDirection: "row",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      size="small"
                      variant={quickCustomerMode ? "contained" : "outlined"}
                      onClick={() => {
                        setQuickCustomerMode(true);
                        setFormData({ ...formData, customerId: "" });
                      }}
                      sx={{
                        fontSize: "0.75rem",
                        px: 2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        transition: "all 0.3s ease",
                        ...(quickCustomerMode
                          ? commonStyles.gradientButton
                          : commonStyles.outlinedButton),
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Neregistrovani korisnik
                    </Button>
                    <Button
                      size="small"
                      variant={!quickCustomerMode ? "contained" : "outlined"}
                      onClick={() => {
                        setQuickCustomerMode(false);
                        setQuickCustomerName("");
                      }}
                      sx={{
                        fontSize: "0.75rem",
                        px: 2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        transition: "all 0.3s ease",
                        ...(!quickCustomerMode
                          ? commonStyles.gradientButton
                          : commonStyles.outlinedButton),
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Registrovan korisnik
                    </Button>
                  </Box>
                )}

                {/* Polje za ime osobe ili izbor klijenta */}
                {(() => {
                  const actualUserType = getUserTypeFromCurrentTenant();
                  return actualUserType && actualUserType !== "customer";
                })() && (
                  <Box sx={{ minWidth: isMobile ? "100%" : 300 }}>
                    {editingAppointment ? (
                      editingAppointment.isQuickAppointment ? (
                        <TextField
                          fullWidth
                          label="Ime osobe"
                          value={editingAppointment.customerName || ""}
                          disabled={true}
                          sx={commonStyles.formField}
                        />
                      ) : (
                        <Autocomplete
                          options={getFilteredCustomers}
                          value={getFilteredCustomers.find((u) => (u._id || u.id) === formData.customerId) || null}
                          getOptionLabel={(option) => option ? `${option.firstName} ${option.lastName}` : getCustomerName(editingAppointment)}
                          disabled={true}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Klijent"
                              sx={commonStyles.formField}
                            />
                          )}
                          sx={{ width: "100%" }}
                        />
                      )
                    ) : currentTenant?.settings?.enableQuickAppointment ? (
                      quickCustomerMode ? (
                        <TextField
                          fullWidth
                          label="Ime osobe"
                          value={isMobile ? quickCustomerNameLocal : (quickCustomerNameRef.current || quickCustomerName || "")}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            quickCustomerNameRef.current = newValue;
                            // Na mobilnom ažuriraj samo lokalni state dok korisnik kuca
                            if (isMobile) {
                              setQuickCustomerNameLocal(newValue);
                            } else {
                              // Na desktopu ažuriraj glavni state odmah
                              setQuickCustomerName(newValue);
                            }
                          }}
                          onBlur={(e) => {
                            const finalValue = e.target.value;
                            // Na mobilnom, sync lokalni state sa glavnim state-om
                            if (isMobile) {
                              setQuickCustomerNameLocal(finalValue);
                              setQuickCustomerName(finalValue);
                            } else {
                              // Na desktopu samo osiguraj da je state ažuriran
                              setQuickCustomerName(finalValue);
                            }
                          }}
                          placeholder="Unesite ime osobe za termin"
                          sx={commonStyles.formField}
                          disabled={editingAppointment}
                        />
                      ) : (
                        <Autocomplete
                          options={getFilteredCustomers}
                          getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                          value={getFilteredCustomers.find((u) => (u._id || u.id) === formData.customerId) || null}
                          onChange={(event, newValue) => {
                            setFormData({
                              ...formData,
                              customerId: newValue ? (newValue._id || newValue.id) : "",
                            });
                          }}
                          disabled={
                            editingAppointment ||
                            (currentUser?.userType === "super_admin" &&
                              !selectedTenantId)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Klijent"
                              sx={commonStyles.formField}
                            />
                          )}
                          sx={{ width: "100%" }}
                        />
                      )
                    ) : (
                      <Autocomplete
                        options={getFilteredCustomers}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                        value={getFilteredCustomers.find((u) => (u._id || u.id) === formData.customerId) || null}
                        onChange={(event, newValue) => {
                          setFormData({
                            ...formData,
                            customerId: newValue ? (newValue._id || newValue.id) : "",
                          });
                        }}
                        disabled={
                          editingAppointment ||
                          (currentUser?.userType === "super_admin" &&
                            !selectedTenantId)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Klijent"
                            sx={commonStyles.formField}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    )}
                  </Box>
                )}

                {/* Zaposleni */}
                {(() => {
                  let actualUserType = currentUser?.userType;
                  let connection = null;
                  
                  if (!actualUserType && currentUser?.tenantConnections?.length > 0) {
                    connection = currentUser.tenantConnections.find(
                      tc => (tc.tenantId?._id || tc.tenantId) === selectedTenantId
                    ) || currentUser.tenantConnections[0];
                    actualUserType = connection?.userType;
                  }
                  
                  const isCustomer = actualUserType === "customer";
                  const isTenantAdmin = actualUserType === "tenant_admin";
                  const isSuperAdmin = actualUserType === "super_admin";
                  
                  const requiresEmployee = (isCustomer || isTenantAdmin || isSuperAdmin) &&
                    !currentTenant?.settings?.autoAssignEmployee &&
                    selectedTenantId;
                  
                  if (!requiresEmployee) return false;
                  
                  // Ne prikazuj dropdown ako ima samo jedan zaposleni
                  const availableEmployees = getAvailableEmployees();
                  const shouldShow = availableEmployees.length > 1;
                  
                  return shouldShow;
                })() && (
                  <Box sx={{ minWidth: isMobile ? "100%" : 250 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={commonStyles.textSecondary}>
                        Zaposleni
                      </InputLabel>
                      <Select
                        value={formData.employeeId}
                        label="Zaposleni"
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            employeeId: e.target.value,
                          })
                        }
                        sx={commonStyles.selectField}
                      >
                        <MenuItem value="">
                          <em>Izaberite zaposlenog</em>
                        </MenuItem>
                        {getAvailableEmployees().map((employee) => (
                            <MenuItem key={employee._id} value={employee._id}>
                              {`${employee.firstName} ${employee.lastName}`}
                              {employee.shift ? ` (${employee.shift})` : ""}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* BODY - transformisan iz DialogContent */}
      <Box sx={{ mb: { xs: 0, md: 4 }, flex: 1 }}>
        {currentStep === 1 && (
          <>
            {currentUser?.userType === "super_admin" && !selectedTenantId ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "400px",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h6" sx={commonStyles.textSecondary}>
                  Molimo izaberite tenant da biste videli kategorije i usluge
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  flex: 1,
                  overflow: "auto",
                }}
              >
                {/* Fiksirane kategorije na vrhu - moderni chip dizajn */}
                <Box
                  sx={{
                    background: isDarkMode
                      ? "linear-gradient(180deg, rgba(30,41,59,0.6) 0%, rgba(30,41,59,0.4) 100%)"
                      : "linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(241,245,249,0.7) 100%)",
                    backdropFilter: "blur(12px)",
                    pb: 2,
                    pt: 2,
                    px: 2,
                    borderRadius: 2,
                    border: `1px solid ${customColors.border.light}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                      }}
                    >
                      Kategorije
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: "1px",
                        background: `linear-gradient(90deg, ${customColors.border.light}, transparent)`,
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      overflowX: "auto",
                      pb: 0.5,
                      "&::-webkit-scrollbar": {
                        height: "4px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: isDarkMode
                          ? "rgba(148, 163, 184, 0.3)"
                          : "rgba(148, 163, 184, 0.4)",
                        borderRadius: "2px",
                        "&:hover": {
                          backgroundColor: isDarkMode
                            ? "rgba(148, 163, 184, 0.5)"
                            : "rgba(148, 163, 184, 0.6)",
                        },
                      },
                    }}
                  >
                    <Chip
                      label="Sve kategorije"
                      onClick={() => {
                        setSelectedCategoryId("");
                        const top = document.getElementById("services-container");
                        if (top) top.scrollTop = 0;
                      }}
                      sx={{
                        height: 36,
                        borderRadius: 2,
                        fontWeight: 500,
                        transition: "all 0.2s",
                        ...(!selectedCategoryId
                          ? {
                              background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                              color: "white",
                              boxShadow: `0 4px 12px ${customColors.primary.main}40`,
                              "&:hover": {
                                boxShadow: `0 6px 16px ${customColors.primary.main}60`,
                                transform: "translateY(-1px)",
                              },
                            }
                          : {
                              backgroundColor: isDarkMode
                                ? "rgba(148, 163, 184, 0.1)"
                                : "rgba(241, 245, 249, 1)",
                              color: commonStyles.textPrimary.color,
                              border: `1px solid ${customColors.border.light}`,
                              "&:hover": {
                                backgroundColor: isDarkMode
                                  ? "rgba(148, 163, 184, 0.15)"
                                  : "rgba(226, 232, 240, 1)",
                              },
                            }),
                      }}
                    />
                    {(Array.isArray(categories) ? categories : []).map((cat) => (
                      <Chip
                        key={cat._id}
                        label={cat.name}
                        onClick={() => {
                          setSelectedCategoryId(cat._id);
                          const ref = categoryRefs.current[cat._id];
                          if (ref) {
                            const container = document.getElementById("services-container");
                            if (container) {
                              const containerRect = container.getBoundingClientRect();
                              const refRect = ref.getBoundingClientRect();
                              const scrollTop =
                                container.scrollTop + (refRect.top - containerRect.top);
                              container.scrollTo({
                                top: scrollTop,
                                behavior: "smooth",
                              });
                            }
                          }
                        }}
                        sx={{
                          height: 36,
                          borderRadius: 2,
                          fontWeight: 500,
                          transition: "all 0.2s",
                          ...(selectedCategoryId === cat._id
                            ? {
                                background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                                color: "white",
                                boxShadow: `0 4px 12px ${customColors.primary.main}40`,
                                "&:hover": {
                                  boxShadow: `0 6px 16px ${customColors.primary.main}60`,
                                  transform: "translateY(-1px)",
                                },
                              }
                            : {
                                backgroundColor: isDarkMode
                                  ? "rgba(148, 163, 184, 0.1)"
                                  : "rgba(241, 245, 249, 1)",
                                color: commonStyles.textPrimary.color,
                                border: `1px solid ${customColors.border.light}`,
                                "&:hover": {
                                  backgroundColor: isDarkMode
                                    ? "rgba(148, 163, 184, 0.15)"
                                    : "rgba(226, 232, 240, 1)",
                                  transform: "translateY(-1px)",
                                },
                              }),
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Usluge grupisane po kategoriji */}
                <Box
                  id="services-container"
                  sx={{
                    minHeight: "400px",
                    maxHeight: isMobile ? "calc(100vh - 500px)" : "600px",
                    overflowY: "auto",
                    pr: 1,
                    mb: { xs: 0, md: 0 },
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.3)",
                      borderRadius: "3px",
                      "&:hover": {
                        backgroundColor: isDarkMode
                          ? "rgba(255, 255, 255, 0.5)"
                          : "rgba(0, 0, 0, 0.5)",
                      },
                    },
                  }}
                >
                  {services.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      {categories.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <CircularProgress size={40} />
                          <Typography variant="body2" sx={{ ...commonStyles.textSecondary, mt: 2 }}>
                            Učitavanje usluga...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ ...commonStyles.textSecondary }}>
                          Nema dostupnih usluga za ovog klijenta
                        </Typography>
                      )}
                    </Box>
                  ) : categories.length === 0 ? (
                    // No categories - show all services without grouping
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {services.map((service) => {
                        const isSelected = selectedServiceIds.includes(service._id);
                        return (
                          <Card
                            key={service._id}
                            ref={(el) => {
                              if (el) {
                                serviceRefs.current[service._id] = el;
                              }
                            }}
                            onClick={() => {
                              setSelectedServiceIds((prev) =>
                                prev.includes(service._id)
                                  ? prev.filter((id) => id !== service._id)
                                  : [...prev, service._id]
                              );
                            }}
                            sx={{
                              p: 2,
                              cursor: "pointer",
                              position: "relative",
                              border: `2px solid ${
                                isSelected
                                  ? customColors.primary.main
                                  : customColors.border.light
                              }`,
                              borderRadius: 3,
                              background: isSelected
                                ? isDarkMode
                                  ? `linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.08) 100%)`
                                  : `linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(37,99,235,0.05) 100%)`
                                : isDarkMode
                                ? "rgba(30,41,59,0.6)"
                                : "rgba(255,255,255,1)",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              boxShadow: isSelected
                                ? `0 8px 24px ${customColors.primary.main}25`
                                : "0 2px 8px rgba(0,0,0,0.04)",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: isSelected
                                  ? `0 12px 32px ${customColors.primary.main}35`
                                  : "0 8px 20px rgba(0,0,0,0.1)",
                                borderColor: customColors.primary.main,
                              },
                            }}
                          >
                            {isSelected && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: `0 2px 8px ${customColors.primary.main}50`,
                                }}
                              >
                                <CheckIcon sx={{ fontSize: 16, color: "white" }} />
                              </Box>
                            )}
                            <Box sx={{ pr: isSelected ? 4 : 0 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  ...commonStyles.textPrimary,
                                  fontWeight: 600,
                                  mb: 0.5,
                                  fontSize: "1rem",
                                }}
                              >
                                {service.name}
                              </Typography>
                              {service.description && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    ...commonStyles.textSecondary,
                                    mb: 1.5,
                                    fontSize: "0.85rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {service.description}
                                </Typography>
                              )}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mt: 1.5,
                                }}
                              >
                                <Chip
                                  icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                                  label={`${service.duration || 30} min`}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: "0.75rem",
                                    backgroundColor: isDarkMode
                                      ? "rgba(148, 163, 184, 0.15)"
                                      : "rgba(241, 245, 249, 1)",
                                    color: commonStyles.textSecondary.color,
                                    "& .MuiChip-icon": {
                                      color: customColors.primary.main,
                                    },
                                  }}
                                />
                                <Typography
                                  variant="h6"
                                  sx={{
                                    ...commonStyles.textPrimary,
                                    fontWeight: 700,
                                    fontSize: "1.1rem",
                                    color: isSelected
                                      ? customColors.primary.main
                                      : commonStyles.textPrimary.color,
                                  }}
                                >
                                  {service.price?.amount
                                    ? `${service.price.amount} ${
                                        service.price.currency || "RSD"
                                      }`
                                    : "0 RSD"}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        );
                      })}
                    </Box>
                  ) : (
                    (Array.isArray(categories) ? categories : []).map((cat) => {
                      const catId = cat._id;
                      const list = servicesByCategory[catId] || [];
                      if (!list.length) return null;
                      return (
                        <Box
                          key={catId}
                          ref={(el) => (categoryRefs.current[catId] = el)}
                          mb={3}
                        >
                          <Box sx={{ mb: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 4,
                                  height: 24,
                                  borderRadius: 2,
                                  background: `linear-gradient(180deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                                }}
                              />
                              <Typography
                                variant="h6"
                                sx={{
                                  ...commonStyles.textPrimary,
                                  fontWeight: 700,
                                  fontSize: "1.15rem",
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {cat.name}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "1fr",
                                  md: "repeat(2, 1fr)",
                                },
                                gap: 2,
                              }}
                            >
                              {list.map((service) => {
                                const isSelected = selectedServiceIds.includes(service._id);
                                return (
                                  <Card
                                    key={service._id}
                                    ref={(el) => {
                                      if (el) {
                                        serviceRefs.current[service._id] = el;
                                      }
                                    }}
                                    onClick={() => {
                                      setSelectedServiceIds((prev) =>
                                        prev.includes(service._id)
                                          ? prev.filter((id) => id !== service._id)
                                          : [...prev, service._id]
                                      );
                                    }}
                                    sx={{
                                      p: 2,
                                      cursor: "pointer",
                                      position: "relative",
                                      border: `2px solid ${
                                        isSelected
                                          ? customColors.primary.main
                                          : customColors.border.light
                                      }`,
                                      borderRadius: 3,
                                      background: isSelected
                                        ? isDarkMode
                                          ? `linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.08) 100%)`
                                          : `linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(37,99,235,0.05) 100%)`
                                        : isDarkMode
                                        ? "rgba(30,41,59,0.6)"
                                        : "rgba(255,255,255,1)",
                                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                      boxShadow: isSelected
                                        ? `0 8px 24px ${customColors.primary.main}25`
                                        : "0 2px 8px rgba(0,0,0,0.04)",
                                      "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: isSelected
                                          ? `0 12px 32px ${customColors.primary.main}35`
                                          : "0 8px 20px rgba(0,0,0,0.1)",
                                        borderColor: customColors.primary.main,
                                      },
                                    }}
                                  >
                                    {isSelected && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 8,
                                          right: 8,
                                          width: 24,
                                          height: 24,
                                          borderRadius: "50%",
                                          background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          boxShadow: `0 2px 8px ${customColors.primary.main}50`,
                                        }}
                                      >
                                        <CheckIcon sx={{ fontSize: 16, color: "white" }} />
                                      </Box>
                                    )}
                                    <Box sx={{ pr: isSelected ? 4 : 0 }}>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          ...commonStyles.textPrimary,
                                          fontWeight: 600,
                                          mb: 0.5,
                                          fontSize: "1rem",
                                        }}
                                      >
                                        {service.name}
                                      </Typography>
                                      {service.description && (
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            ...commonStyles.textSecondary,
                                            mb: 1.5,
                                            fontSize: "0.85rem",
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {service.description}
                                        </Typography>
                                      )}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          mt: 1.5,
                                        }}
                                      >
                                        <Chip
                                          icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                                          label={`${service.duration || 30} min`}
                                          size="small"
                                          sx={{
                                            height: 24,
                                            fontSize: "0.75rem",
                                            backgroundColor: isDarkMode
                                              ? "rgba(148, 163, 184, 0.15)"
                                              : "rgba(241, 245, 249, 1)",
                                            color: commonStyles.textSecondary.color,
                                            "& .MuiChip-icon": {
                                              color: customColors.primary.main,
                                            },
                                          }}
                                        />
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            ...commonStyles.textPrimary,
                                            fontWeight: 700,
                                            fontSize: "1.1rem",
                                            color: isSelected
                                              ? customColors.primary.main
                                              : commonStyles.textPrimary.color,
                                          }}
                                        >
                                          {service.price?.amount
                                            ? `${service.price.amount} ${
                                                service.price.currency || "RSD"
                                              }`
                                            : "0 RSD"}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Card>
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            )}
          </>
        )}

        {currentStep === 2 && selectedServiceIds.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                flex: isMobile ? "none" : 1,
                width: isMobile ? "100%" : "auto",
                "& .MuiPickersPopper-root": {
                  "& .MuiPaper-root": {
                    backgroundColor: isDarkMode
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f8f9fa !important",
                    border: isDarkMode
                      ? "1px solid rgba(255, 255, 255, 0.15)"
                      : "1px solid #dee2e6 !important",
                    boxShadow: isDarkMode
                      ? "0 8px 32px rgba(0, 0, 0, 0.5)"
                      : "0 8px 32px rgba(0, 0, 0, 0.2) !important",
                  },
                },
                "& .MuiDateCalendar-root": {
                  backgroundColor: isDarkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#f8f9fa !important",
                  color: isDarkMode
                    ? "rgba(255, 255, 255, 0.9)"
                    : "#212529 !important",
                  "& .MuiPickersDay-root": {
                    backgroundColor: isDarkMode
                      ? "transparent"
                      : "#ffffff !important",
                    color: isDarkMode
                      ? "rgba(255, 255, 255, 0.9)"
                      : "#212529 !important",
                    border: isDarkMode
                      ? "none"
                      : "1px solid #e9ecef !important",
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.1)"
                        : "#e9ecef !important",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#1976d2 !important",
                      color: "#ffffff !important",
                    },
                    "&.MuiPickersDay-today": {
                      border: "2px solid #1976d2 !important",
                    },
                  },
                  "& .MuiDayCalendar-header .MuiTypography-root": {
                    color: isDarkMode
                      ? "rgba(255, 255, 255, 0.7)"
                      : "#495057 !important",
                  },
                  "& .MuiPickersDay-dayOutsideMonth": {
                    color: isDarkMode
                      ? "rgba(255, 255, 255, 0.3)"
                      : "#adb5bd !important",
                  },
                },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Datum termina"
                  value={formData.appointmentDate}
                  onChange={(newDate) => {
                    const nextDate = combineDateAndTime(
                      newDate,
                      selectedTime
                    );
                    setFormData({ ...formData, appointmentDate: nextDate });
                  }}
                  minDate={tomorrow}
                  shouldDisableDate={(date) => isDateNonWorking(date)}
                  renderDay={(day, _value, DayComponentProps) => {
                    const isFullyBooked = isDateFullyBooked(day);
                    if (isFullyBooked) {
                      console.log('Rendering fully booked day:', day, DayComponentProps);
                    }
                    return (
                      <PickersDay
                        {...DayComponentProps}
                        className={isFullyBooked ? 'fully-booked-day' : DayComponentProps.className}
                        data-fully-booked={isFullyBooked ? 'true' : undefined}
                        sx={{
                          ...(isFullyBooked && {
                            color: '#d32f2f !important',
                            opacity: '1 !important',
                            '&.Mui-disabled': {
                              color: '#d32f2f !important',
                              opacity: '1 !important',
                              cursor: 'not-allowed',
                            },
                          }),
                        }}
                      />
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} sx={commonStyles.formField} />
                  )}
                  PopperProps={{
                    sx: {
                      "& .MuiPaper-root": {
                        backgroundColor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "#f8f9fa !important",
                        border: isDarkMode
                          ? "1px solid rgba(255, 255, 255, 0.15)"
                          : "1px solid #dee2e6 !important",
                        boxShadow: isDarkMode
                          ? "0 8px 32px rgba(0, 0, 0, 0.5)"
                          : "0 8px 32px rgba(0, 0, 0, 0.2) !important",
                      },
                      // Jači selektori za fully_booked datume - samo crvena boja teksta
                      "& .fully-booked-day": {
                        color: '#d32f2f !important',
                      },
                      "& .fully-booked-day.Mui-disabled": {
                        color: '#d32f2f !important',
                        opacity: '1 !important',
                        cursor: 'not-allowed',
                      },
                      "& .MuiPickersDay-root[data-fully-booked='true']": {
                        color: '#d32f2f !important',
                      },
                      "& .MuiPickersDay-root[data-fully-booked='true'].Mui-disabled": {
                        color: '#d32f2f !important',
                        opacity: '1 !important',
                        cursor: 'not-allowed',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              {/* Legenda za fully_booked datume */}
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    border: '1px solid #d32f2f',
                    display: 'inline-block',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#6c757d',
                  }}
                >
                  Crvena boja označava da je dan ceo zauzet
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: isMobile ? "none" : 1,
                width: isMobile ? "100%" : "auto",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={commonStyles.textSecondary}
                mb={1}
              >
                Izaberite vreme:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(4, 1fr)",
                    sm: "repeat(5, 1fr)",
                    md: "repeat(6, 1fr)",
                  },
                  gap: 1.5,
                  maxHeight: isMobile ? "200px" : "300px",
                  overflowY: "auto",
                  p: 0.5,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: isDarkMode
                      ? "rgba(148, 163, 184, 0.3)"
                      : "rgba(148, 163, 184, 0.4)",
                    borderRadius: "3px",
                    "&:hover": {
                      backgroundColor: isDarkMode
                        ? "rgba(148, 163, 184, 0.5)"
                        : "rgba(148, 163, 184, 0.6)",
                    },
                  },
                }}
              >
                {availableSlots &&
                Array.isArray(availableSlots.data?.availableSlots) &&
                availableSlots.data.availableSlots.length === 0 ? (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      textAlign: "center",
                      py: 4,
                    }}
                  >
                    <Typography
                      sx={{
                        ...commonStyles.textSecondary,
                        fontSize: "0.9rem",
                      }}
                    >
                      Nema dostupnih termina za izabrani datum.
                    </Typography>
                  </Box>
                ) : (
                  timeSlots.map((slot) => {
                    const isSelected = selectedTime === slot;
                    return (
                      <Button
                        key={slot}
                        variant={isSelected ? "contained" : "outlined"}
                        size="small"
                        onClick={() => {
                          setSelectedTime(slot);
                          const nextDate = combineDateAndTime(
                            formData.appointmentDate,
                            slot
                          );
                          setFormData({
                            ...formData,
                            appointmentDate: nextDate,
                          });
                        }}
                        sx={{
                          minWidth: "auto",
                          py: 1.25,
                          borderRadius: 2.5,
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          textTransform: "none",
                          transition: "all 0.2s",
                          ...(isSelected
                            ? {
                                background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                                color: "white",
                                boxShadow: `0 4px 12px ${customColors.primary.main}40`,
                                "&:hover": {
                                  boxShadow: `0 6px 16px ${customColors.primary.main}60`,
                                  transform: "translateY(-2px)",
                                  background: `linear-gradient(135deg, ${customColors.primary.dark}, ${customColors.primary.main})`,
                                },
                              }
                            : {
                                borderColor: customColors.border.light,
                                color: commonStyles.textPrimary.color,
                                backgroundColor: isDarkMode
                                  ? "rgba(148, 163, 184, 0.08)"
                                  : "rgba(241, 245, 249, 1)",
                                "&:hover": {
                                  borderColor: customColors.primary.main,
                                  backgroundColor: isDarkMode
                                    ? "rgba(59, 130, 246, 0.15)"
                                    : "rgba(59, 130, 246, 0.08)",
                                  transform: "translateY(-2px)",
                                  boxShadow: `0 4px 12px ${customColors.primary.main}20`,
                                },
                              }),
                        }}
                      >
                        {slot}
                      </Button>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>
        )}

        {currentStep === 2 && (
          <>
            <TextField
              fullWidth
              label="Beleška (napomena)"
              multiline
              rows={4}
              value={formData.notes.customer}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  notes: { ...formData.notes, customer: e.target.value },
                })
              }
              placeholder="Unesite dodatne informacije ili napomene za termin..."
              sx={{ ...commonStyles.formField, mb: 2 }}
            />
          </>
        )}

        {/* Korak 3: Pregled */}
        {currentStep === 3 && (
          <Box
            sx={{
              minHeight: "400px",
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
              p: { xs: 2, md: 3 },
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDarkMode
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(0, 0, 0, 0.3)",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(255, 255, 255, 0.5)"
                    : "rgba(0, 0, 0, 0.5)",
                },
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h4"
                sx={{
                  ...commonStyles.textPrimary,
                  fontWeight: 700,
                  mb: 1,
                  background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Pregled rezervacije
              </Typography>
              <Typography
                variant="body2"
                sx={{ ...commonStyles.textSecondary, fontSize: "0.9rem" }}
              >
                Proverite sve detalje pre kreiranja termina
              </Typography>
            </Box>

            {/* Grid layout za kartice */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                },
                gap: 2.5,
              }}
            >
              {/* Informacije o klijentu */}
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${customColors.border.light}`,
                  background: isDarkMode
                    ? "rgba(30,41,59,0.6)"
                    : "rgba(255,255,255,1)",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{ color: "white", fontWeight: 700, fontSize: "1.2rem" }}
                    >
                      {(currentTenant?.settings?.enableQuickAppointment &&
                      quickCustomerMode
                        ? quickCustomerName || "N"
                        : `${
                            users.find((u) => u._id === formData.customerId)
                              ?.firstName?.[0] || ""
                          }${
                            users.find((u) => u._id === formData.customerId)
                              ?.lastName?.[0] || ""
                          }`) || "N"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ ...commonStyles.textSecondary, fontSize: "0.75rem" }}
                    >
                      {currentTenant?.settings?.enableQuickAppointment &&
                      quickCustomerMode
                        ? "Osoba"
                        : "Klijent"}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      {currentTenant?.settings?.enableQuickAppointment &&
                      quickCustomerMode
                        ? quickCustomerName || "Nije uneseno ime"
                        : `${
                            users.find((u) => u._id === formData.customerId)
                              ?.firstName || ""
                          } ${
                            users.find((u) => u._id === formData.customerId)
                              ?.lastName || ""
                          }`}
                    </Typography>
                  </Box>
                </Box>
                {currentUser?.userType === "super_admin" &&
                  selectedTenantId && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${customColors.border.light}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ ...commonStyles.textSecondary, fontSize: "0.75rem" }}
                      >
                        Tenant
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          ...commonStyles.textPrimary,
                          fontWeight: 500,
                          mt: 0.5,
                        }}
                      >
                        {tenants.find((t) => t._id === selectedTenantId)?.name}
                      </Typography>
                    </Box>
                  )}
              </Card>

              {/* Datum i vreme */}
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${customColors.border.light}`,
                  background: isDarkMode
                    ? "rgba(30,41,59,0.6)"
                    : "rgba(255,255,255,1)",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <ScheduleIcon
                    sx={{
                      fontSize: 32,
                      color: customColors.primary.main,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ ...commonStyles.textSecondary, fontSize: "0.75rem" }}
                    >
                      Datum i vreme
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        ...commonStyles.textPrimary,
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      {formData.appointmentDate
                        ? formData.appointmentDate.toLocaleDateString("sr-RS")
                        : "Nije odabran datum"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        ...commonStyles.textSecondary,
                        mt: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      {selectedTime || "Nije odabrano vreme"}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>

            {/* Izabrane usluge */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${customColors.border.light}`,
                background: isDarkMode
                  ? "rgba(30,41,59,0.6)"
                  : "rgba(255,255,255,1)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  ...commonStyles.textPrimary,
                  mb: 2.5,
                  fontWeight: 600,
                  fontSize: "1.1rem",
                }}
              >
                Izabrane usluge
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {selectedServiceIds.map((serviceId) => {
                  const service = services.find((s) => s._id === serviceId);
                  const category = categories.find(
                    (c) => c._id === (service?.categoryId?._id || service?.categoryId)
                  );
                  return (
                    <Box
                      key={serviceId}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: isDarkMode
                          ? "rgba(148, 163, 184, 0.08)"
                          : "rgba(241, 245, 249, 1)",
                        border: `1px solid ${customColors.border.light}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            ...commonStyles.textPrimary,
                            fontWeight: 600,
                          }}
                        >
                          {service?.name}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: customColors.primary.main,
                            fontWeight: 700,
                            fontSize: "1rem",
                          }}
                        >
                          {service?.price?.amount || 0} RSD
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        {category && (
                          <Chip
                            label={category.name}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: "0.75rem",
                              backgroundColor: isDarkMode
                                ? "rgba(148, 163, 184, 0.15)"
                                : "rgba(241, 245, 249, 1)",
                            }}
                          />
                        )}
                        <Chip
                          icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                          label={`${service?.duration || 30} min`}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: "0.75rem",
                            backgroundColor: isDarkMode
                              ? "rgba(148, 163, 184, 0.15)"
                              : "rgba(241, 245, 249, 1)",
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Card>

            {/* Ukupno */}
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${customColors.primary.main}, ${customColors.primary.dark})`,
                boxShadow: `0 8px 32px ${customColors.primary.main}40`,
                color: "white",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "white", mb: 2.5, fontWeight: 700 }}
              >
                Ukupno
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(3, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
                  >
                    Broj usluga
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: 700, mt: 0.5 }}
                  >
                    {selectedServiceIds.length}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
                  >
                    Ukupno vreme
                    {additionalTime > 0 && (
                      <span style={{ fontSize: "0.7rem", opacity: 0.8 }}>
                        {" "}(+{additionalTime} min)
                      </span>
                    )}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: 700, mt: 0.5 }}
                  >
                    {totalDuration} min
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}
                  >
                    Ukupna cena
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: 700, mt: 0.5 }}
                  >
                    {totalPrice} RSD
                  </Typography>
                </Box>
              </Box>
              {/* Dodatno vreme - samo za admin/zaposleni/super admin */}
              {(() => {
                const actualUserType = getUserTypeFromCurrentTenant();
                const isSuperAdmin = actualUserType === "super_admin";
                const isTenantAdmin = actualUserType === "tenant_admin";
                const isEmployee = actualUserType === "employee";
                
                if (isSuperAdmin || isTenantAdmin || isEmployee) {
                  return (
                    <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Dodatno vreme (minute)"
                        value={additionalTime != null ? additionalTime : ""}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          setAdditionalTime(value >= 0 ? value : 0);
                        }}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            step: 1
                          }
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            color: "white",
                            "& fieldset": {
                              borderColor: "rgba(255, 255, 255, 0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(255, 255, 255, 0.5)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "rgba(255, 255, 255, 0.7)",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(255, 255, 255, 0.7)",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "rgba(255, 255, 255, 0.9)",
                          },
                        }}
                        helperText="Dodatno vreme će biti dodato na ukupno vreme termina"
                        FormHelperTextProps={{
                          sx: { color: "rgba(255, 255, 255, 0.6)" }
                        }}
                      />
                    </Box>
                  );
                }
                return null;
              })()}
            </Card>

            {/* Status i napomene */}
            {(formData.status || formData.notes?.customer) && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: `1px solid ${customColors.primary.main}`,
                  borderRadius: 2,
                  mt: 3,
                }}
              >
                {formData.status && (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ ...commonStyles.textPrimary, mb: 1 }}
                    >
                      Status
                    </Typography>
                    <Typography sx={commonStyles.textSecondary}>
                      {formData.status === "pending"
                        ? "Na čekanju"
                        : formData.status === "confirmed"
                        ? "Potvrđeno"
                        : formData.status === "cancelled"
                        ? "Otkazano"
                        : formData.status}
                    </Typography>
                  </>
                )}

                {formData.notes?.customer && (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ ...commonStyles.textPrimary, mb: 1, mt: 2 }}
                    >
                      Napomene
                    </Typography>
                    <Typography sx={commonStyles.textSecondary}>
                      {formData.notes.customer}
                    </Typography>
                  </>
                )}
              </Box>
            )}
            </Box>
          </Box>
        )}
      </Box>
          </Grid>

          {/* DESNA STRANA - Summary Sidebar (simplified) */}
          {!isMobile && (
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  position: "sticky",
                  top: 64,
                  zIndex: 1101,
                  border: `1px solid ${customColors.border.light}`,
                  borderRadius: 2,
                  background: isDarkMode
                    ? "rgba(17,24,39,0.5)"
                    : "rgba(255,255,255,0.9)",
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: "calc(100vh - 80px)",
                }}
              >
                <Box sx={{ p: 2, borderBottom: `1px solid ${customColors.border.light}` }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ ...commonStyles.textSecondary, mb: 1, fontWeight: 600, letterSpacing: 0.2 }}
                  >
                    Pregled
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />

                  {/* Jednostavne, kompaktne stavke */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Tenant (samo super admin) */}
                  {(() => {
                    const actualUserType = getUserTypeFromCurrentTenant();
                    const isSuperAdmin = actualUserType === "super_admin";
                    if (!isSuperAdmin || !selectedTenantId) return null;
                    return (
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <Typography sx={{ ...commonStyles.textSecondary }}>Tenant</Typography>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 600 }}>
                          {tenants.find((t) => t._id === selectedTenantId)?.name || "-"}
                        </Typography>
                      </Box>
                    );
                  })()}

                  {/* Klijent / Osoba */}
                  {(() => {
                    const actualUserType = getUserTypeFromCurrentTenant();
                    const isCustomer = actualUserType === "customer";
                    const name = isCustomer
                      ? `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim()
                      : quickCustomerMode && quickCustomerName.trim()
                      ? quickCustomerName.trim()
                      : formData.customerId
                      ? (() => {
                          const u = users.find((x) => x._id === formData.customerId);
                          return u ? `${u.firstName} ${u.lastName}` : "";
                        })()
                      : "";
                    if (!name) return null;
                    return (
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <Typography sx={{ ...commonStyles.textSecondary }}>
                          {isCustomer || (quickCustomerMode && quickCustomerName.trim()) ? "Osoba" : "Klijent"}
                        </Typography>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 600 }}>{name}</Typography>
                      </Box>
                    );
                  })()}

                  {/* Zaposleni */}
                  {(() => {
                    if (currentTenant?.settings?.autoAssignEmployee) return null;
                    if (!formData.employeeId) return null;
                    const u = users.find((x) => x._id === formData.employeeId);
                    if (!u) return null;
                    return (
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <Typography sx={{ ...commonStyles.textSecondary }}>Zaposleni</Typography>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 600 }}>
                          {`${u.firstName} ${u.lastName}`}
                        </Typography>
                      </Box>
                    );
                  })()}

                  {/* Usluge */}
                  {selectedServiceIds.length > 0 && (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <Typography sx={{ ...commonStyles.textSecondary }}>Usluge</Typography>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 600 }}>
                          {selectedServiceIds.length}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                        {selectedServiceIds.slice(0, 4).map((serviceId) => {
                          const service = services.find((s) => s._id === serviceId);
                          if (!service) return null;
                          const lineRight = `${service.duration || 30}m${service.price?.amount ? ` • ${service.price.amount} RSD` : ""}`;
                          return (
                            <Box key={serviceId} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                              <Typography sx={{ ...commonStyles.textPrimary, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                                {service.name}
                              </Typography>
                              <Typography sx={{ ...commonStyles.textSecondary, fontSize: 12, fontWeight: 600 }}>
                                {lineRight}
                              </Typography>
                            </Box>
                          );
                        })}
                        {selectedServiceIds.length > 4 && (
                          <Typography sx={{ ...commonStyles.textSecondary, fontSize: 12 }}>+{selectedServiceIds.length - 4} više</Typography>
                        )}
                      </Box>
                    </>
                  )}

                  {/* Datum i vreme */}
                  {(formData.appointmentDate || selectedTime) && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                      <Typography sx={{ ...commonStyles.textSecondary }}>Termin</Typography>
                      <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 600 }}>
                        {formData.appointmentDate
                          ? formData.appointmentDate.toLocaleDateString("sr-RS")
                          : ""}
                        {selectedTime ? ` • ${selectedTime}` : ""}
                      </Typography>
                    </Box>
                  )}

                  {/* Ukupno vreme i cena */}
                  {(totalDuration > 0 || totalPrice > 0) && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <Typography sx={{ ...commonStyles.textSecondary }}>Vreme</Typography>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 700 }}>{totalDuration} min</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <Typography sx={{ ...commonStyles.textSecondary }}>Cena</Typography>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 700 }}>{totalPrice} RSD</Typography>
                      </Box>
                    </>
                  )}

                  {/* Placeholders */}
                  {!selectedTenantId &&
                    selectedServiceIds.length === 0 &&
                    !formData.appointmentDate &&
                    !selectedTime && (
                      <Typography sx={{ ...commonStyles.textSecondary, fontSize: 12, mt: 0.5 }}>
                        Kako birate opcije, ovde se prikazuje sažetak.
                      </Typography>
                    )}
                </Box>
                </Box>
                
                {/* FOOTER - unutar desne strane za desktop */}
                <Box
                  sx={{
                    mt: 'auto',
                    py: 2.5,
                    px: 3,
                    background: isDarkMode
                      ? "linear-gradient(135deg, rgba(37, 99, 235, 0.18), rgba(17, 24, 39, 0.6))"
                      : "linear-gradient(135deg, rgba(59, 130, 246, 0.14), rgba(255,255,255,0.7))",
                    backdropFilter: "blur(10px)",
                    borderTop: `1px solid ${customColors.border.light}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    gap: 2,
                    boxShadow: isDarkMode
                      ? "0 -4px 20px rgba(0,0,0,0.3)"
                      : "0 -4px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      width: "100%",
                      justifyContent: "flex-start",
                      flexDirection: "row",
                    }}
                  >
                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      sx={{
                        ...commonStyles.cancelButton,
                        px: 3,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Otkaži
                    </Button>
                    {currentStep === 1 ? (
                      <Button
                        onClick={() => setCurrentStep(2)}
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        disabled={
                          (() => {
                            let actualUserType = currentUser?.userType;
                            if (!actualUserType && currentUser?.tenantConnections?.length > 0) {
                              const connection = currentUser.tenantConnections.find(
                                tc => (tc.tenantId?._id || tc.tenantId) === selectedTenantId
                              ) || currentUser.tenantConnections[0];
                              actualUserType = connection?.userType;
                            }
                            
                            const isCustomer = actualUserType === "customer";
                            const isSuperAdmin = actualUserType === "super_admin";
                            const isEmployee = actualUserType === "employee";
                            
                            let hasCustomerOrQuickName = false;
                            if (!isCustomer) {
                              if (currentTenant?.settings?.enableQuickAppointment && quickCustomerMode) {
                                hasCustomerOrQuickName = quickCustomerName.trim().length > 0;
                              } else {
                                hasCustomerOrQuickName = !!formData.customerId;
                              }
                            } else {
                              hasCustomerOrQuickName = true;
                            }
                            
                            return selectedServiceIds.length === 0 ||
                              (!isCustomer && !hasCustomerOrQuickName) ||
                              (isSuperAdmin && !selectedTenantId) ||
                              (!isEmployee &&
                                !currentTenant?.settings?.autoAssignEmployee &&
                                !formData.employeeId);
                          })()
                        }
                        sx={{
                          ...commonStyles.gradientButton,
                          px: 3,
                          borderRadius: 2,
                          textTransform: "none",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: `0 8px 20px ${customColors.primary.main}50`,
                          },
                        }}
                      >
                        Dalje
                      </Button>
                    ) : currentStep === 2 ? (
                      <>
                        <Button
                          onClick={() => setCurrentStep(1)}
                          startIcon={<ArrowBackIcon />}
                          variant="outlined"
                          sx={{
                            px: 3,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            borderColor: customColors.border.light,
                            color: commonStyles.textPrimary.color,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              borderColor: customColors.primary.main,
                            },
                          }}
                        >
                          Nazad
                        </Button>
                        <Button
                          onClick={() => setCurrentStep(3)}
                          variant="contained"
                          endIcon={<CheckIcon />}
                          disabled={!selectedTime}
                          sx={{
                            ...commonStyles.gradientButton,
                            px: 3,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 8px 20px ${customColors.primary.main}50`,
                            },
                          }}
                        >
                          Pregled
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          onClick={() => setCurrentStep(2)}
                          startIcon={<ArrowBackIcon />}
                          variant="outlined"
                          sx={{
                            px: 3,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            borderColor: customColors.border.light,
                            color: commonStyles.textPrimary.color,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              borderColor: customColors.primary.main,
                            },
                          }}
                        >
                          Nazad
                        </Button>
                        {(() => {
                          // Prikaži dugme za otkazivanje samo za customer korisnike u edit modu
                          const actualUserType = getUserTypeFromCurrentTenant();
                          const isCustomer = actualUserType === 'customer';
                          return (
                            isCustomer && editingAppointment && formData.status !== 'cancelled' && (
                              <Button
                                onClick={handleCancelAppointment}
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                sx={{
                                  mr: 1,
                                  px: 3,
                                  borderRadius: 2,
                                  textTransform: "none",
                                  fontSize: "0.875rem",
                                  fontWeight: 600,
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    transform: "translateY(-2px)",
                                  },
                                }}
                              >
                                Otkaži termin
                              </Button>
                            )
                          );
                        })()}
                        <Button
                          onClick={handleSubmit}
                          variant="contained"
                          startIcon={editingAppointment ? <SaveIcon /> : <AddIcon />}
                          disabled={submitting}
                          sx={{
                            ...commonStyles.gradientButton,
                            px: 3,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 8px 20px ${customColors.primary.main}50`,
                            },
                          }}
                        >
                          {editingAppointment ? "Sačuvaj" : "Kreiraj"}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
      
      {/* FOOTER - van Grid-a za mobilni prikaz */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            py: 2,
            px: 2,
            background: isDarkMode
              ? "linear-gradient(135deg, rgba(37, 99, 235, 0.95), rgba(17, 24, 39, 0.95))"
              : "linear-gradient(135deg, rgba(59, 130, 246, 0.95), rgba(255,255,255,0.95))",
            backdropFilter: "blur(10px)",
            borderTop: `1px solid ${customColors.border.light}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "column",
            gap: 2,
            boxShadow: isDarkMode
              ? "0 -4px 20px rgba(0,0,0,0.5)"
              : "0 -4px 20px rgba(0,0,0,0.15)",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              width: "100%",
              justifyContent: "stretch",
              flexDirection: "column",
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              startIcon={<CancelIcon />}
              sx={{
                ...commonStyles.cancelButton,
                px: 3,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                transition: "all 0.3s ease",
                width: "100%",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              Otkaži
            </Button>
            {currentStep === 1 ? (
              <Button
                onClick={() => setCurrentStep(2)}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                disabled={
                  (() => {
                    let actualUserType = currentUser?.userType;
                    if (!actualUserType && currentUser?.tenantConnections?.length > 0) {
                      const connection = currentUser.tenantConnections.find(
                        tc => (tc.tenantId?._id || tc.tenantId) === selectedTenantId
                      ) || currentUser.tenantConnections[0];
                      actualUserType = connection?.userType;
                    }
                    
                    const isCustomer = actualUserType === "customer";
                    const isSuperAdmin = actualUserType === "super_admin";
                    const isEmployee = actualUserType === "employee";
                    
                    let hasCustomerOrQuickName = false;
                    if (!isCustomer) {
                      if (currentTenant?.settings?.enableQuickAppointment && quickCustomerMode) {
                        const quickName = quickCustomerNameRef.current || quickCustomerName || "";
                        hasCustomerOrQuickName = quickName.trim().length > 0;
                      } else {
                        hasCustomerOrQuickName = !!formData.customerId;
                      }
                    } else {
                      hasCustomerOrQuickName = true;
                    }
                    
                    const isDisabled = selectedServiceIds.length === 0 ||
                      (!isCustomer && !hasCustomerOrQuickName) ||
                      (isSuperAdmin && !selectedTenantId) ||
                      (!isEmployee &&
                        !currentTenant?.settings?.autoAssignEmployee &&
                        !formData.employeeId);
                    
                    return isDisabled;
                  })()
                }
                sx={{
                  ...commonStyles.gradientButton,
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  transition: "all 0.3s ease",
                  width: "100%",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 20px ${customColors.primary.main}50`,
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                Dalje
              </Button>
            ) : currentStep === 2 ? (
              <>
                <Button
                  onClick={() => setCurrentStep(1)}
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  sx={{
                    px: 3,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    borderColor: customColors.border.light,
                    color: commonStyles.textPrimary.color,
                    transition: "all 0.3s ease",
                    width: "100%",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor: customColors.primary.main,
                    },
                  }}
                >
                  Nazad
                </Button>
                <Button
                  onClick={() => setCurrentStep(3)}
                  variant="contained"
                  endIcon={<CheckIcon />}
                  disabled={!selectedTime}
                  sx={{
                    ...commonStyles.gradientButton,
                    px: 3,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    transition: "all 0.3s ease",
                    width: "100%",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 20px ${customColors.primary.main}50`,
                    },
                  }}
                >
                  Pregled
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setCurrentStep(2)}
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  sx={{
                    px: 3,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    borderColor: customColors.border.light,
                    color: commonStyles.textPrimary.color,
                    transition: "all 0.3s ease",
                    width: "100%",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor: customColors.primary.main,
                    },
                  }}
                >
                  Nazad
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  startIcon={editingAppointment ? <SaveIcon /> : <AddIcon />}
                  disabled={submitting}
                  sx={{
                    ...commonStyles.gradientButton,
                    px: 3,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    transition: "all 0.3s ease",
                    width: "100%",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 8px 20px ${customColors.primary.main}50`,
                    },
                  }}
                >
                  {editingAppointment ? "Sačuvaj" : "Kreiraj"}
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default AppointmentBooking;
